import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ApiRequest } from '../common/services/api-request';

export interface AppSettings {
  highlight_notes?: string;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class SettingsStateService {
  private readonly cacheKey = 'safarizineAppSettings';
  private apiRequest = inject(ApiRequest);
  private settingsSubject = new BehaviorSubject<AppSettings | null>(this.getCachedSettings());
  private settingsLoaded = !!this.settingsSubject.value;

  settings$ = this.settingsSubject.asObservable();

  setSettings(data: AppSettings | null) {
    this.settingsSubject.next(data);
    this.settingsLoaded = true;
    this.setCachedSettings(data);
  }

  async refreshSettings(force = false) {
    if (this.settingsLoaded && !force) {
      return this.settingsSubject.value;
    }

    const response = await firstValueFrom(this.apiRequest.getSettings());
    const settings = this.normalizeSettings(response);
    this.setSettings(settings);
    return settings;
  }

  private normalizeSettings(response: any): AppSettings {
    return response?.data?.settings ?? response?.settings ?? response?.data ?? response ?? {};
  }

  private getCachedSettings(): AppSettings | null {
    try {
      const cachedSettings = localStorage.getItem(this.cacheKey);
      return cachedSettings ? JSON.parse(cachedSettings) : null;
    } catch {
      localStorage.removeItem(this.cacheKey);
      return null;
    }
  }

  private setCachedSettings(data: AppSettings | null) {
    if (!data) {
      localStorage.removeItem(this.cacheKey);
      return;
    }

    localStorage.setItem(this.cacheKey, JSON.stringify(data));
  }
}
